import type { WorkspaceFeatureFlags } from './Workspace'

// Enhanced Company Models for Multi-Tenant System
// Maintains backward compatibility while adding new features

/** Optional hex colors stored in Parse `Company.settings` (Company Management branding). */
export interface CompanyBrandingSettings {
  accent_color?: string
  link_color?: string
  surface_tint_color?: string
}

/** Parse `Company.settings` object shape (cloud merges these keys). */
export interface CompanyJsonSettings extends CompanyBrandingSettings {
  auto_assignment_enabled?: boolean
  /** Same values as CompanyUserRole; kept as string so this file can sit above the enum. */
  default_role?: string
  /** The default role's key (SC-439): `member`, `admin` or a custom role's key. */
  default_role_key?: string
}

export interface Company {
  // Core Information
  id: string
  name: string
  domain: string
  subdomain?: string
  
  // Branding
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  platform_name?: string
  platform_url?: string

  /** Parse `Company.settings` JSON (auto_assignment, default_role, optional branding hex fields). */
  settings?: CompanyJsonSettings
  
  // Configuration
  allowed_domains?: string[] // Domains that can auto-assign users to this company
  auto_assignment_enabled?: boolean // Whether to auto-assign users based on email domain
  /** Default role for auto-assigned users: a legacy role string, or a custom role's key (SC-439). */
  default_role?: CompanyUserRole | string
  max_users?: number
  max_rooms?: number
  features?: CompanyFeatures
  
  // Status
  status: 'active' | 'inactive' | 'suspended' | 'trial' | 'past_due'
  is_active: boolean
  /** Per-company chat backend toggle: true = Matrix, false = Parse (default). */
  matrix_chat_enabled?: boolean
  /** Matrix chat tenant slug → `<slug>.<CHAT_HS_BASE_DOMAIN>` is the homeserver (chat uses this, not `subdomain`). */
  matrix_chat_domain?: string
  
  // Billing & Subscription (New)
  billing_tier: BillingTier
  subscription_plan_id?: string
  subscription_plan?: SubscriptionPlan
  billing_cycle: 'monthly' | 'yearly'
  per_user_pricing: boolean
  base_price: number
  price_per_user: number
  
  // Trial & Subscription Dates
  trial_ends_at?: Date
  subscription_ends_at?: Date
  next_billing_date?: Date
  
  // Usage Tracking (New)
  current_user_count: number
  current_room_count: number
  storage_used_mb: number
  last_activity_at?: Date
  
  // Metadata
  created_by?: string
  created_at: Date
  updated_at: Date
}

export enum BillingTier {
  FREE = 'free',
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise',
  CUSTOM = 'custom'
}

export interface SubscriptionPlan {
  id: string
  name: string
  tier: BillingTier
  base_price: number
  price_per_user: number
  max_users: number
  max_rooms: number
  features: CompanyFeatures
  addons: string[] // Addon IDs
  description: string
  is_active: boolean
  created_at: Date
  updated_at: Date
}

export interface CompanyFeatures {
  // Core Features
  mapEditor?: boolean
  chat?: boolean
  whiteboard?: boolean
  teleport?: boolean
  screenSharing?: boolean
  videoCalling?: boolean
  customBranding?: boolean
  advancedAnalytics?: boolean
  apiAccess?: boolean
  
  // Addon Features (New)
  advancedSecurity?: boolean
  sso?: boolean
  customIntegrations?: boolean
  prioritySupport?: boolean
  dedicatedServer?: boolean
  advancedReporting?: boolean
  customWorkflows?: boolean
  dataExport?: boolean
  
  // Limits (New)
  maxStorageGb?: number
  maxApiCallsPerDay?: number
  maxVideoParticipants?: number
  maxChatHistory?: number
  maxFileSizeMb?: number
}

export interface Addon {
  id: string
  name: string
  description: string
  category: 'feature' | 'integration' | 'support' | 'storage'
  pricing_type: 'per_user' | 'flat_rate' | 'usage_based'
  price: number
  features: Partial<CompanyFeatures>
  dependencies: string[] // Other addon IDs
  is_active: boolean
  created_at: Date
  updated_at: Date
}

export interface CompanyAddon {
  id: string
  company_id: string
  addon_id: string
  addon?: Addon
  status: 'active' | 'inactive' | 'pending'
  quantity?: number // For usage-based addons
  price_override?: number
  added_at: Date
  expires_at?: Date
  added_by: string
}

export interface CompanyUser {
  // Existing fields
  id: string
  user_id: string
  company_id: string
  role: CompanyUserRole
  /**
   * The role's key (SC-439): `member`, `admin`, `owner` or a custom role's key. Missing on an old
   * cloud; `role` is then the only source (`memberRoleKey`).
   */
  roleKey?: string | null
  /**
   * The Role's current name (a platform admin may rename any role), so a custom role reads as
   * itself and not as the legacy `role` string. Missing on an old cloud: `memberRoleLabel`.
   */
  role_name?: string | null
  /**
   * The role that applies when the stored one is missing or unusable (the company or platform
   * default, else Member), and whether this row is using it. Display only: never read as an
   * explicit grant. Missing on an old cloud.
   */
  effective_role_key?: string | null
  role_is_default?: boolean
  permissions: string[]
  joined_at: Date
  is_active: boolean
  invited_by?: string
  invitation_status: InvitationStatus
  department?: string
  title?: string
  last_active?: Date
  auto_assigned?: boolean // Flag to indicate if user was auto-assigned
  migrated?: boolean // Flag to indicate if user was migrated
  
  // New licensing fields
  license_type?: 'active' | 'inactive' | 'suspended'
  license_cost?: number
  billing_status?: 'billed' | 'unbilled' | 'waived'
  license_addons?: string[] // Addon IDs
  
  user?: {
    id: string
    username: string
    email: string
    /** True once the invite link has been redeemed (or the address verified). */
    emailVerified?: boolean
    /** True once the user has chosen a password of their own. */
    hasManualPassword?: boolean
    /** True when Google Sign-In is attached. */
    googleLinked?: boolean
    /**
     * True while an invitation token is still on the account — written when the invite
     * is sent, cleared once a password is set. The signal behind
     * `companyUserHasOutstandingInvitation`. [SC-313]
     */
    hasOutstandingInvitation?: boolean
  }
  profile?: {
    id: string
    firstName?: string
    lastName?: string
    /** Shown instead of first + last name when set (SC-438). */
    displayName?: string
    title?: string
    role?: string
    /** Pattern style for the generated avatar ('custom' while a photo is set). */
    avatar?: string
    customAvatarUrl?: string
    gameCharacter?: string
  }
}

/**
 * True when this company member still has an unfinished invitation — the row that
 * should be offered a fresh invite rather than a password reset.
 *
 * Keyed off the presence of `companyInvitationToken`, which means exactly this and
 * nothing else. The token is written when the invite is sent and burned in
 * `completeCompanyInvitation`, i.e. only once a password exists — so it covers both
 * the never-clicked case and the clicked-then-abandoned-the-password-step case.
 *
 * An earlier version keyed off `hasManualPassword !== true`, which mislabelled every
 * account predating that flag. `hasManualPassword` is only written by `enhancedSignUp`,
 * `resetPasswordWithToken` and `setAccountPassword`, so a long-active user who
 * registered before it existed has a real password, no flag, and would have been
 * offered "Resend invite". The token carries no such history. [SC-313]
 */
export function companyUserHasOutstandingInvitation(member: Pick<CompanyUser, 'user'>): boolean {
  return member.user?.hasOutstandingInvitation === true
}

export type CompanyUserStatus = 'invited' | 'inactive' | 'active'

/**
 * Status for the admin Users list.
 *
 * The column used to read `is_active` alone, which the invite path sets to true the
 * moment an invitation is sent — so someone who had never opened their link showed as
 * "Active". `invitation_status` cannot substitute: every creation path writes the
 * literal 'accepted', so it carries no information.
 *
 * Deactivation is reported ahead of an outstanding invitation: a disabled member
 * cannot get in either way, so that is the more useful fact about the row. [SC-313]
 */
export function companyUserStatus(
  member: Pick<CompanyUser, 'user' | 'is_active'>,
): CompanyUserStatus {
  if (member.is_active !== true) return 'inactive'
  if (companyUserHasOutstandingInvitation(member)) return 'invited'
  return 'active'
}

export interface UserLicense {
  id: string
  company_id: string
  user_id: string
  license_type: 'active' | 'inactive' | 'suspended'
  base_cost: number
  addon_costs: Record<string, number>
  total_cost: number
  billing_cycle: 'monthly' | 'yearly'
  effective_date: Date
  expires_at?: Date
  created_at: Date
}

export enum CompanyUserRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  MODERATOR = 'moderator',
  USER = 'user'
}

export enum InvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  EXPIRED = 'expired'
}

export interface CompanyInvitation {
  id: string
  company_id: string
  email: string
  role: CompanyUserRole
  permissions: string[]
  invited_by: string
  invited_at: Date
  expires_at: Date
  status: InvitationStatus
}

export interface Invoice {
  id: string
  company_id: string
  invoice_number: string
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'
  amount: number
  tax_amount: number
  total_amount: number
  currency: string
  billing_cycle: 'monthly' | 'yearly'
  period_start: Date
  period_end: Date
  due_date: Date
  paid_at?: Date
  items: InvoiceItem[]
  created_at: Date
  updated_at: Date
}

export interface InvoiceItem {
  id: string
  invoice_id: string
  description: string
  quantity: number
  unit_price: number
  total_price: number
  item_type: 'subscription' | 'addon' | 'overage' | 'adjustment'
  reference_id?: string // CompanyUser ID, Addon ID, etc.
}

export interface UsageMetrics {
  id: string
  company_id: string
  date: Date
  active_users: number
  total_users: number
  active_rooms: number
  total_rooms: number
  storage_used_mb: number
  api_calls: number
  video_minutes: number
  chat_messages: number
  created_at: Date
}

// Legacy interfaces for backward compatibility
export interface CompanySettings {
  name: string
  domain: string
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  platform_name?: string
  platform_url?: string
  allowed_domains?: string[]
  max_users?: number
  max_rooms?: number
  features?: CompanyFeatures
  registration_enabled?: boolean
}

export interface CompanyStats {
  total_users: number
  active_users: number
  total_rooms: number
  active_rooms: number
  total_messages: number
  storage_used: number
  last_activity: Date
}

export interface CompanyWorkspaceSettings {
  id: string
  company_id: string
  name: string
  platform_name?: string
  platform_url?: string
  logo_url?: string
  allowed_domains?: string[]
  registration_enabled?: boolean
  urls: Record<string, string>
  /** The same application-wide switches as `WorkspaceSettings.features`, not the per-company `CompanyFeatures`. */
  features?: WorkspaceFeatureFlags
  restrictions?: {
    maxRoomsPerUser?: number
    maxUsersPerRoom?: number
  }
  is_active: boolean
  created_by?: string
  updated_by?: string
  created_at: Date
  updated_at: Date
} 